import { zodResolver } from "@hookform/resolvers/zod";
import { APP_CAREPROVIDER } from "core/consts";
import { Env } from "core/types";
import { Button, ControlledInput, ControlledPasswordInput } from "ds/ui";
import {
  LoginErrorState,
  LoginView,
} from "dsl/ecosystems/CareproviderOnboardingLoginPage/useHandleLogin";
import { LoginPageVariants } from "dsl/ecosystems/LoginPageV2/classes";
import { LoginPageLayout } from "dsl/ecosystems/LoginPageV2/LoginPageLayout";
import { useLoginFieldSideEffects } from "dsl/ecosystems/LoginPageV2/useLoginFieldSideEffects";
import { useCallback, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslations } from "translations";
import { getCareproviderLoginDefaultValues } from "./getDefaultValues";
import {
  CareproviderLoginFormValues,
  CareproviderLoginParsedValues,
  CareproviderLoginSubmitPayload,
  getCareproviderLoginSchema,
} from "./schema";

export type CareproviderLoginFormProps = LoginPageVariants & {
  challengeTimestamp: string | undefined;
  contactUsUrl?: string;
  env: Env;
  goToForgotPassword: string;
  goToProductionUrl: string;
  isLoading: boolean;
  loginErrors?: LoginErrorState;
  loginView: LoginView;
  onBackToLogin: () => void;
  onSubmit: (payload: CareproviderLoginSubmitPayload) => void;
  otherAppUrl?: string;
  resetError: () => void;
};

export function CareproviderLoginForm({
  app = APP_CAREPROVIDER,
  challengeTimestamp,
  contactUsUrl,
  env,
  goToForgotPassword,
  goToProductionUrl,
  isLoading,
  loginErrors,
  loginView = "login",
  onBackToLogin,
  onSubmit,
  otherAppUrl,
  resetError,
}: CareproviderLoginFormProps) {
  const translations = useTranslations();

  const isLogin = loginView === "login";

  const methods = useForm<CareproviderLoginFormValues>({
    defaultValues: getCareproviderLoginDefaultValues(),
    resolver: async (values, context, options) => {
      const schema = getCareproviderLoginSchema({
        translations,
        view: loginView,
      });
      return zodResolver(schema)(values, context, options);
    },
  });

  useLoginFieldSideEffects({ resetError, watch: methods.watch });

  useEffect(() => {
    (["email", "password", "challenge"] as const).forEach((field) => {
      const externalMessage = loginErrors?.[field];
      if (externalMessage) {
        methods.setError(field, { message: externalMessage });
      } else if (methods.getFieldState(field).error?.type === "manual") {
        methods.clearErrors(field);
      }
    });
  }, [loginErrors, methods]);

  const handleValid = useCallback(
    (values: CareproviderLoginParsedValues) => {
      onSubmit(values);
    },
    [onSubmit],
  );

  const handleBackToLogin = useCallback(() => {
    methods.reset(getCareproviderLoginDefaultValues());
    onBackToLogin();
  }, [methods, onBackToLogin]);

  const submitButtonId = isLogin ? "login" : "login_challenge";

  return (
    <FormProvider {...methods}>
      <LoginPageLayout
        app={app}
        autoFocus="back-button"
        challengeField={
          <ControlledInput
            autoComplete="off"
            label={translations.login.loginPage.verificationCode}
            name="challenge"
            placeholder={
              translations.login.loginPage.verificationCodePlaceholder
            }
            required
            type={isLogin ? "hidden" : "text"}
          />
        }
        challengeTimestamp={challengeTimestamp}
        contactUsUrl={contactUsUrl}
        emailField={
          <ControlledInput
            autoComplete="email"
            label={translations.login.loginPage.email}
            name="email"
            placeholder={translations.login.loginPage.emailPlaceholder}
            required
            type={isLogin ? "email" : "hidden"}
          />
        }
        env={env}
        goToForgotPassword={goToForgotPassword}
        goToProductionUrl={goToProductionUrl}
        isLoading={isLoading}
        loginErrors={loginErrors}
        loginView={loginView}
        otherAppUrl={otherAppUrl}
        passwordField={
          <ControlledPasswordInput
            autoComplete={isLogin ? "current-password" : "off"}
            label={translations.login.loginPage.password}
            name="password"
            placeholder={translations.login.loginPage.passwordPlaceholder}
            required
            type={isLogin ? "password" : "hidden"}
          />
        }
        submit={
          <Button
            color="primary"
            fullWidth
            id={submitButtonId}
            isLoading={isLoading}
            size="lg"
            type="submit"
            variant="solid"
          >
            {translations.login.loginPage.loginButton}
          </Button>
        }
        onBackToLogin={handleBackToLogin}
        onSubmit={methods.handleSubmit(handleValid)}
      />
    </FormProvider>
  );
}
